import { CUSTOM_LABEL_PREFIX } from '@this+that/constants';
import { LabelId } from '@this+that/models';

/**
 * Set of label IDs that should always be aggregated across thread messages.
 * These labels appear at thread level if ANY message has them.
 */
export const ALWAYS_AGGREGATE_LABELS = new Set([
  LabelId.SENT,
  LabelId.TRASH,
  LabelId.SPAM,
  LabelId.DRAFT,
  LabelId.IMPORTANT,
  LabelId.STARRED,
  LabelId.INBOX,
  LabelId.UNREAD,
]);

/**
 * Set of CATEGORY_* label IDs.
 * These labels should be inherited from the first message in the thread that has them.
 */
export const CATEGORY_LABELS = new Set([
  LabelId.CATEGORY_FORUMS,
  LabelId.CATEGORY_UPDATES,
  LabelId.CATEGORY_PROMOTIONS,
  LabelId.CATEGORY_PERSONAL,
  LabelId.CATEGORY_SOCIAL,
]);

/**
 * Aggregates label IDs from multiple messages in a thread.
 *
 * Strategy:
 * - Special case: If ALL older messages have TRASH:
 *   - Keep only TRASH + labels from most recent message
 *   - Skips normal aggregation (creates "clean slate")
 * - Normal case:
 *   - Starts with ALL labels from the most recent message
 *   - Selects CATEGORY_* label with priority:
 *     1. CATEGORY from first message (newest→oldest) that has INBOX
 *     2. CATEGORY from most recent message (if present)
 *     3. First CATEGORY found in any older message
 *   - Aggregates ALWAYS_AGGREGATE_LABELS from all messages
 * - Cleanup rules (both cases):
 *   - If SPAM present: removes both INBOX and SENT
 *
 * Performance: O(N*M) where N = number of messages, M = average labels per message
 *
 * Examples:
 * - Most recent: [SENT], Older: [INBOX, CATEGORY_PERSONAL], [TRASH, CATEGORY_PERSONAL]
 *   → [INBOX, SENT, CATEGORY_PERSONAL, TRASH]
 *   (Normal aggregation: not all older have TRASH)
 *
 * - Most recent: [TRASH], Older: [SENT, TRASH], [CATEGORY_PERSONAL, INBOX, TRASH]
 *   → [TRASH]
 *   (All older have TRASH: keep only TRASH + most recent labels)
 *
 * - Most recent: [DRAFT], Older: [TRASH], [SENT, TRASH], [CATEGORY_PERSONAL, TRASH]
 *   → [TRASH, DRAFT]
 *   (All older have TRASH: keep only TRASH + DRAFT from most recent)
 *
 * @param mostRecentMessageLabels - Label IDs from the most recent message in the thread
 * @param otherMessages - Array of older messages with their labelIds (sorted newest to oldest)
 * @returns Aggregated array of unique label IDs
 */
export const aggregateThreadLabelIds = (
  mostRecentMessageLabels: LabelId[],
  otherMessages: Array<{ labelIds?: LabelId[] }>,
): LabelId[] => {
  // Check if ALL older messages have TRASH (for special handling) - do this first to avoid unnecessary work
  const allOlderMessagesHaveTrash =
    otherMessages.length > 0 &&
    otherMessages.every((msg) => msg.labelIds?.includes(LabelId.TRASH));

  if (allOlderMessagesHaveTrash) {
    const result = new Set<LabelId>(mostRecentMessageLabels);
    result.add(LabelId.TRASH);
    if (result.has(LabelId.SPAM)) {
      result.delete(LabelId.INBOX);
      result.delete(LabelId.SENT);
    }
    return Array.from(result);
  }

  // Build aggregated labels and find category in a single pass over most recent message
  const aggregatedLabels = new Set<LabelId>();
  let categoryFromMostRecent: LabelId | null = null;

  for (const label of mostRecentMessageLabels) {
    aggregatedLabels.add(label);
    if (!categoryFromMostRecent && CATEGORY_LABELS.has(label)) {
      categoryFromMostRecent = label;
    }
  }

  // Track CATEGORY selection state
  let selectedCategory: LabelId | null = null;
  let categoryFromInboxMessage: LabelId | null = null;
  let categoryFromAnyMessage: LabelId | null = null;

  // If most recent has both INBOX and CATEGORY, it's our winner
  const mostRecentHasInbox = aggregatedLabels.has(LabelId.INBOX);
  if (categoryFromMostRecent && mostRecentHasInbox) {
    selectedCategory = categoryFromMostRecent;
  }

  // Scan older messages for CATEGORY selection and label aggregation
  for (const message of otherMessages) {
    if (!message.labelIds?.length) {
      continue;
    }

    const messageHasInbox = message.labelIds.includes(LabelId.INBOX);

    for (const labelId of message.labelIds) {
      const isCategory = CATEGORY_LABELS.has(labelId);

      // Look for CATEGORY in message with INBOX (highest priority if not found yet)
      if (!categoryFromInboxMessage && isCategory && messageHasInbox) {
        categoryFromInboxMessage = labelId;
      }

      // Track first CATEGORY found anywhere (lowest priority fallback)
      if (!categoryFromAnyMessage && isCategory) {
        categoryFromAnyMessage = labelId;
      }

      // Always aggregate these labels from all messages
      if (ALWAYS_AGGREGATE_LABELS.has(labelId)) {
        aggregatedLabels.add(labelId);
      }

      // Also aggregate custom labels (Label_*) from all messages
      if (labelId.startsWith(CUSTOM_LABEL_PREFIX)) {
        aggregatedLabels.add(labelId);
      }
    }
  }

  // Apply CATEGORY priority selection
  if (!selectedCategory) {
    selectedCategory =
      categoryFromInboxMessage ||
      categoryFromMostRecent ||
      categoryFromAnyMessage;
  }

  // Remove any CATEGORY labels from aggregatedLabels and add the selected one
  for (const categoryLabel of CATEGORY_LABELS) {
    aggregatedLabels.delete(categoryLabel);
  }

  if (selectedCategory) {
    aggregatedLabels.add(selectedCategory);
  }

  // FIX #16: Was `if (SPAM && !INBOX)` which only removed SENT but never removed INBOX.
  // When a thread had SPAM + INBOX from older messages, both labels coexisted, causing misclassification.
  // SPAM removes INBOX and SENT
  if (aggregatedLabels.has(LabelId.SPAM)) {
    aggregatedLabels.delete(LabelId.INBOX);
    aggregatedLabels.delete(LabelId.SENT);
  }

  return Array.from(aggregatedLabels);
};
